import { isAddBgColorAtom } from "@/atoms/app";
import { jotaiStore } from "@/atoms/store";
import { miQsWeatherDomains } from "@/constants/domain.config";
import { getCurrentAdScenario } from "@/hooks/useAdScenario";
import { getImg } from "@/utils";
import { applyGooglePublisherTagPageConfig } from "@/components/common/GooglePublisherTag/pageLevelConfig";
import classNames from "classnames";
import { nanoid } from "nanoid";
import { shouldPauseMqsGoogleAdRequest, useMqsInterstitialGateSnapshot } from "./mqsInterstitialGate";

// 需要懒加载域名列表
const lazyLoadDomains1 = ["en-miwg.comfortweather.com", "en-wg.comfortweather.com"];
const lazyLoadDomains2 = [
  "tr-mils.comfortweather.com",
  "vi-mils.comfortweather.com",
  "pt-br-mils.comfortweather.com",
  "pt-mils.comfortweather.com",
  "es-mils.comfortweather.com",
  "arabic-mils.comfortweather.com",
  "fr-mils.comfortweather.com",
  "it-mils.comfortweather.com",
  "urdu-mils.comfortweather.com",
  "th-mils.comfortweather.com",
  "en-mils.comfortweather.com",
  "jp-mils.comfortweather.com",
  "ger-mils.comfortweather.com",
  "id-mils.comfortweather.com",
  "po-mils.comfortweather.com",
  "bn-mils.comfortweather.com",
  "el-mils.comfortweather.com",
  "he-mils.comfortweather.com",
  "ko-mils.comfortweather.com",
  "uk-mils.comfortweather.com",
  "ru-mils.comfortweather.com",
  "az-mils.comfortweather.com",
  "hu-mils.comfortweather.com",
  "ro-mils.comfortweather.com",
  "sr-mils.comfortweather.com",
  "bg-mils.comfortweather.com",
  "sk-mils.comfortweather.com",
  "zh-hant-mils.comfortweather.com",
  "si-mils.comfortweather.com",
  "cs-mils.comfortweather.com",
];
const lazyLoadPositions1 = ["position-23"];
const lazyLoadPositions2 = ["position-21", "position-34", "position-35", "position-36"];
const lazyLoadPositions5 = ["position-21", "position-34", "position-35", "position-36"];
const lazyLoadPositions6 = ["position-21", "position-34", "position-35", "position-36"];
const tranChannelViewportLazyPositions = ["position-34", "position-35", "position-36"];

type AdStatus = "idle" | "pending" | "filled" | "unfilled";

function getSlotHeight(style: Record<string, any> | undefined, rect: number[] | undefined) {
  const slotHeightValue = String(style?.height ?? style?.minHeight ?? rect?.[1] ?? "0");
  const slotHeightMatch = slotHeightValue.match(/(\d+(?:\.\d+)?)/);
  return slotHeightMatch ? `${slotHeightMatch[1]}px` : "0px";
}

/**
 * 说明：
 * `www + tran_ls_tab` 需要按“首个广告立即加载，第二个起再懒加载”的口径执行，
 * 因此这里优先走组件级视口观察，而不是继续复用原先只按 host/position 硬编码的 GPT 全局懒加载。
 */
function resolveViewportLazyLoad({
  explicitIsLazyLoad,
  dataPosition,
  isTranTabChannelScenario,
}: {
  explicitIsLazyLoad?: boolean;
  dataPosition: string;
  isTranTabChannelScenario: boolean;
}) {
  if (typeof explicitIsLazyLoad === "boolean") {
    return explicitIsLazyLoad;
  }

  if (isTranTabChannelScenario) {
    return tranChannelViewportLazyPositions.includes(dataPosition);
  }

  return false;
}

function resolveLegacyGptLazyLoad({
  hostName,
  dataPosition,
  isTranTabAdScenario,
  isTranTabChannelScenario,
  useViewportLazyLoad,
}: {
  hostName: string;
  dataPosition: string;
  isTranTabAdScenario: boolean;
  isTranTabChannelScenario: boolean;
  useViewportLazyLoad: boolean;
}) {
  if (useViewportLazyLoad) {
    return false;
  }

  const isLazyLoadGroup1 = lazyLoadDomains1.includes(hostName) && lazyLoadPositions1.includes(dataPosition);
  const isLazyLoadGroup2 = lazyLoadDomains2.includes(hostName) && lazyLoadPositions2.includes(dataPosition);
  const isLazyLoadGroup4 =
    isTranTabAdScenario && !isTranTabChannelScenario && tranChannelViewportLazyPositions.includes(dataPosition);
  const isLazyLoadGroup5 = [...domainMap["v-ls"]].includes(hostName) && lazyLoadPositions5.includes(dataPosition);
  const isLazyLoadGroup6 = miQsWeatherDomains.includes(hostName) && lazyLoadPositions6.includes(dataPosition);
  return isLazyLoadGroup1 || isLazyLoadGroup2 || isLazyLoadGroup4 || isLazyLoadGroup5 || isLazyLoadGroup6;
}

function initializeGoogleAd({
  slot,
  rect,
  id,
  dataPosition,
  enableGptLazyLoad,
  onStatusChange,
}: {
  slot: string;
  rect: number[];
  id: string;
  dataPosition: string;
  enableGptLazyLoad: boolean;
  onStatusChange: (status: AdStatus) => void;
}) {
  const slotRenderListener = (event: any) => {
    if (event?.slot?.getSlotElementId?.() !== id) {
      return;
    }

    if (dataPosition === "position-01" && !event.isEmpty) {
      jotaiStore.set(isAddBgColorAtom, true);
    }

    onStatusChange(event.isEmpty ? "unfilled" : "filled");
  };

  window.googletag = window.googletag || { cmd: [] };
  window.googletag.cmd.push(() => {
    const pubads = window.googletag.pubads();
    pubads.addEventListener("slotRenderEnded", slotRenderListener);

    const existingSlots = pubads.getSlots();
    const isSlotDefined = existingSlots.some((slotItem: any) => slotItem.getSlotElementId() === id);
    if (!isSlotDefined) {
      window.googletag.defineSlot(slot, rect, id)?.addService(pubads);
      window.googletag.setConfig({ collapseDiv: "ON_NO_FILL" });
      if (enableGptLazyLoad) {
        window.googletag.setConfig({
          lazyLoad: {
            fetchMarginPercent: 20,
            renderMarginPercent: 0,
            mobileScaling: 1.0,
          },
        });
      }
      applyGooglePublisherTagPageConfig();
      window.googletag.enableServices();
    }
  });

  return () => {
    window.googletag?.cmd?.push?.(() => {
      window.googletag?.pubads?.()?.removeEventListener?.("slotRenderEnded", slotRenderListener);
      const slots = window.googletag?.pubads?.()?.getSlots?.() ?? [];
      const slotToDestroy = slots.find((slotItem: any) => slotItem.getSlotElementId() === id);
      if (slotToDestroy) {
        window.googletag?.destroySlots?.([slotToDestroy]);
      }
    });
  };
}

export default function Adx1(props: any) {
  const {
    id: propId,
    rect,
    slot,
    style,
    dataPosition,
    isLazyLoad,
    rootMargin = "100px 0px 100px 0px",
  } = props;
  const { hostName } = useHostName();
  const { isTranTabAdScenario, isTranTabChannelScenario } = getCurrentAdScenario();
  const mqsInterstitialGate = useMqsInterstitialGateSnapshot();
  const [uniqueId] = useState(`${propId}${nanoid(4)}`);
  const [isMounted, setIsMounted] = useState(false);
  const [isClosed, setIsClosed] = useState(false);
  const [adStatus, setAdStatus] = useState<AdStatus>("idle");
  const [isInView, setIsInView] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const hasDisplayedRef = useRef(false);
  const slotHeight = getSlotHeight(style, rect);

  if (isTranTabAdScenario) {
    import("./style.css");
  }

  const useViewportLazyLoad = resolveViewportLazyLoad({
    explicitIsLazyLoad: isLazyLoad,
    dataPosition,
    isTranTabChannelScenario,
  });
  const shouldPauseForMqsInterstitial = shouldPauseMqsGoogleAdRequest(
    hostName,
    mqsInterstitialGate.suppressDisplayRequests,
  );
  const enableLegacyGptLazyLoad = resolveLegacyGptLazyLoad({
    hostName,
    dataPosition,
    isTranTabAdScenario,
    isTranTabChannelScenario,
    useViewportLazyLoad,
  });

  useEffect(() => {
    setIsMounted(true);
    return () => {
      setIsMounted(false);
    };
  }, []);

  useEffect(() => {
    if (!isMounted) {
      return;
    }

    if (!useViewportLazyLoad) {
      setIsInView(true);
      return;
    }

    observerRef.current = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observerRef.current?.disconnect();
            observerRef.current = null;
          }
        });
      },
      {
        rootMargin,
        threshold: 0,
      },
    );

    if (containerRef.current) {
      observerRef.current.observe(containerRef.current);
    }

    return () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
    };
  }, [isMounted, rootMargin, useViewportLazyLoad]);

  useEffect(() => {
    if (!isMounted || !isInView || hasDisplayedRef.current || shouldPauseForMqsInterstitial) {
      return;
    }

    // m-qs 第二轮及以后仅在 interstitial 正在展示时暂停普通 GPT 请求；
    // ready 缓存态不能拦截新页面广告，否则会留下空占位。
    hasDisplayedRef.current = true;
    setAdStatus("pending");

    try {
      const cleanupGoogleAd = initializeGoogleAd({
        slot,
        rect,
        id: uniqueId,
        dataPosition,
        enableGptLazyLoad: enableLegacyGptLazyLoad,
        onStatusChange: setAdStatus,
      });

      window.googletag.cmd.push(() => {
        if (document.getElementById(uniqueId)) {
          window.googletag.display(uniqueId);
          console.log("adx1广告display:", uniqueId);
        } else {
          console.warn(`Element with id ${uniqueId} not found in DOM`);
          setAdStatus("unfilled");
        }
      });

      return () => {
        cleanupGoogleAd();
      };
    } catch (error) {
      console.error("adx1广告代码执行出错:", error);
      setAdStatus("unfilled");
    }
  }, [dataPosition, enableLegacyGptLazyLoad, isInView, isMounted, rect, shouldPauseForMqsInterstitial, slot, uniqueId]);

  const adNode = (
    <div ref={containerRef} style={{ textAlign: "center" }} className="center">
      <div id={uniqueId} style={style}></div>
    </div>
  );

  if (isTranTabAdScenario) {
    const shouldShowTranCloseBar = adStatus === "filled";
    const tranWrapperClassName = classNames([
      "ad-section-wrapper",
      adStatus === "pending" && "is-pending",
      adStatus === "filled" && "is-filled",
      adStatus === "unfilled" && "is-empty",
    ]);
    const tranWrapperStyle = {
      "--tran-ad-slot-height": slotHeight,
    } as any;

    return (
      !isClosed && (
        <div className={tranWrapperClassName} data-position={dataPosition} style={tranWrapperStyle}>
          {shouldShowTranCloseBar ? (
            <div className={classNames(["ad-tran-close-bar bg-#f0f0f0 h-4 relative"])}>
              <img
                src={getImg("close_ad.png")}
                alt=""
                className="rotate-180 absolute right-0 top-[0px]"
                style={{
                  width: "37px",
                  height: "21px",
                  top: "-21px",
                }}
                onClick={() => setIsClosed(true)}
              />
            </div>
          ) : null}
          <div className="ad-tran-content">{adNode}</div>
        </div>
      )
    );
  }

  return adNode;
}
